/**
 * `/settings/teams` — manage the active org's teams.
 *
 * Teams group sites (each site belongs to exactly one) and are the
 * unit org-admins hand out access by. Every org ships with a single
 * "Default" team; this is where you add more, rename them, or remove
 * the ones you don't need.
 *
 * Renders into the settings layout's <Island> (no Island here). Reads
 * the team list off the workspace bootstrap; mutations live in
 * `lib/queries/teams.ts` and invalidate the workspace on success.
 *
 * Admin-only: the API rejects non-admin writes, and we hide the write
 * affordances for members so they don't hit a 403 wall.
 */
import { ConfirmDialog } from '@/components/primitives/ConfirmDialog';
import { EmptyState } from '@/components/primitives/EmptyState';
import { PageActions } from '@/components/primitives/PageActions';
import { TeamFormDialog } from '@/components/teams/TeamFormDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import type { WorkspaceOrgTeam } from '@/lib/api-types';
import { toastError, toastSuccess } from '@/lib/errors';
import { useActiveOrg, useDeleteTeam, useWorkspace } from '@/lib/queries';
import { CaretRight, PencilSimple, Plus, Trash, UsersThree } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { type ReactElement, useState } from 'react';

export const Route = createFileRoute('/_app/settings/teams/')({
  component: TeamsSettings,
});

function TeamsSettings(): ReactElement {
  const workspace = useWorkspace();
  const { orgId } = useActiveOrg();
  const activeOrg = workspace.data?.organisations.find((o) => o.auth_provider_id === orgId) ?? null;
  const isAdmin = activeOrg?.role === 'admin';
  const orgUuid = activeOrg?.uuid ?? '';
  const teams = activeOrg?.teams ?? [];

  const del = useDeleteTeam();
  const [createOpen, setCreateOpen] = useState(false);
  const [toEdit, setToEdit] = useState<WorkspaceOrgTeam | null>(null);
  const [toDelete, setToDelete] = useState<WorkspaceOrgTeam | null>(null);

  // The org must always keep at least one team — sites need somewhere
  // to live — so the last remaining team can't be deleted.
  const canDelete = isAdmin && teams.length > 1;

  async function onDeleteConfirm(): Promise<void> {
    if (!toDelete) return;
    try {
      await del.mutateAsync({ teamUuid: toDelete.uuid });
      toastSuccess('Team deleted', toDelete.name);
      setToDelete(null);
    } catch (err) {
      toastError('Failed to delete team', (err as Error)?.message);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-[var(--color-ink)]">Teams</h2>
          <p className="text-sm text-[var(--color-mute)]">
            Group sites into teams for access and billing. {isAdmin ? '' : 'Only admins can edit.'}
          </p>
        </div>
        {isAdmin && (
          <PageActions>
            <Button onClick={() => setCreateOpen(true)} disabled={!orgUuid}>
              <Plus aria-hidden="true" />
              New team
            </Button>
          </PageActions>
        )}
      </header>

      {workspace.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : teams.length === 0 ? (
        <EmptyState
          icon={UsersThree}
          title="No teams yet"
          description="Create a team to start grouping sites."
          action={
            isAdmin ? (
              <Button onClick={() => setCreateOpen(true)} disabled={!orgUuid}>
                <Plus aria-hidden="true" />
                New team
              </Button>
            ) : undefined
          }
        />
      ) : (
        <section className="overflow-hidden rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-[var(--color-line-soft)] border-b text-left text-[var(--color-mute)] text-xs uppercase tracking-[0.06em]">
                  <th className="px-4 py-2 font-medium">Team</th>
                  <th className="px-4 py-2 font-medium">Slug</th>
                  <th className="w-px px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {teams.map((t) => (
                  <tr key={t.uuid} className="border-[var(--color-line-soft)]/70 border-b">
                    <td className="px-4 py-3 font-medium text-[var(--color-ink)]">
                      <Link
                        to="/settings/teams/$teamUuid"
                        params={{ teamUuid: t.uuid }}
                        className="inline-flex items-center gap-1 hover:text-[var(--color-accent-brand)]"
                      >
                        {t.name}
                        <CaretRight aria-hidden="true" className="text-[var(--color-mute)]" />
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-[var(--color-mute)] text-xs">
                      {t.slug}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {isAdmin && (
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setToEdit(t)}
                            aria-label={`Rename ${t.name}`}
                          >
                            <PencilSimple aria-hidden="true" />
                            Rename
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setToDelete(t)}
                            disabled={!canDelete}
                            title={canDelete ? undefined : 'An org must keep at least one team.'}
                            aria-label={`Delete ${t.name}`}
                          >
                            <Trash aria-hidden="true" />
                            Delete
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <TeamFormDialog open={createOpen} onOpenChange={setCreateOpen} orgUuid={orgUuid} />
      <TeamFormDialog
        open={toEdit !== null}
        onOpenChange={(next) => {
          if (!next) setToEdit(null);
        }}
        orgUuid={orgUuid}
        team={toEdit}
      />
      <ConfirmDialog
        open={toDelete !== null}
        onOpenChange={(next) => {
          if (!next) setToDelete(null);
        }}
        title="Delete this team?"
        description={
          <>
            <strong>{toDelete?.name ?? 'This team'}</strong> will be removed. Any sites in it stay
            tracked but will no longer belong to a team. This can't be undone from the dashboard.
          </>
        }
        confirmLabel="Delete team"
        destructive
        loading={del.isPending}
        onConfirm={onDeleteConfirm}
      />
    </div>
  );
}
